import {
  useState,
  useEffect,
  Children,
  cloneElement,
  ReactElement,
  useRef,
  useCallback,
  HTMLProps,
} from "react";
import {
  makeStyles,
  Theme,
  createStyles,
  Popper,
  Button,
  Fade,
  Paper,
  SvgIcon,
  ClickAwayListener,
  Box,
  ButtonBase,
} from "@material-ui/core";
import { format } from "date-fns";

import { Filter as FiltersIcon, Clear as ClearIcon } from "~components/Icons";

import { FilterBase } from "./Filter";

import { StatusFilter } from "./statusFilter";
import { AmountFilter } from "./amountFilter";
import { DateFilter } from "./dateFilter";

export type { StatusFilterProps, StatusFilterOption } from "./statusFilter";

export type FiltersType = {
  status?: string[];
  date?: {
    type: "anytime" | "range";
    from: Date | null;
    to: Date | null;
  };
  amount?: {
    type: "any" | "range";
    from: number | null;
    to: number | null;
  };
};

export type AllowedElement = typeof StatusFilter | typeof AmountFilter | typeof DateFilter;

export type FiltersProps = {
  open: boolean;
  filters: FiltersType;
  setOpen: (to?: boolean) => void;
  onFiltersChange: (filters: FiltersType) => void;
  children: ReactElement<AllowedElement> | ReactElement<AllowedElement>[];
} & HTMLProps<HTMLDivElement>;

export const Filters = ({
  setOpen,
  open,
  onFiltersChange,
  filters,
  children,
  ...rootProps
}: FiltersProps) => {
  const [filtersCount, setFiltersCount] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const classes = useStyles();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpen();
  };

  const handleChange = (update: Partial<FiltersType>) => onFiltersChange({ ...filters, ...update });

  const handleClear = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onFiltersChange({});
    },
    [onFiltersChange],
  );

  useEffect(() => {
    let count = filters?.status && filters?.status?.length > 0 ? 1 : 0;
    if (filters?.date?.from || filters?.date?.to) {
      count += 1;
    }
    if (filters?.amount?.from || filters?.amount?.to) {
      count += 1;
    }
    setFiltersCount(count);
  }, [filters.status, filters.date, filters.amount]);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className={classes.root} ref={rootRef} {...rootProps}>
        <Popper
          open={open}
          anchorEl={rootRef.current}
          placement="bottom-start"
          style={{ zIndex: 1400 }}
          modifiers={{
            preventOverflow: { enabled: true, boundariesElement: "viewport" },
          }}
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper
                style={{
                  boxShadow:
                    "0 10px 12px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(10, 31, 68, 0.1)",
                }}
              >
                <div className={classes.contentRoot}>
                  {Children.map(children, (child: ReactElement, index: number) => {
                    switch (child.type) {
                      case StatusFilter:
                        return (
                          <FilterBase
                            label="Status"
                            index={index}
                            defaultExpanded={index === 0}
                            subHeading={
                              filters?.status?.length ? `${filters?.status?.length} selected` : null
                            }
                          >
                            {cloneElement(child, {
                              filter: filters.status,
                              setFilter: handleChange,
                            })}
                          </FilterBase>
                        );
                      case DateFilter:
                        return (
                          <FilterBase
                            label="Date"
                            index={index}
                            defaultExpanded={index === 0}
                            subHeading={
                              filters?.date?.from || filters?.date?.to
                                ? `${
                                    filters?.date?.from
                                      ? format(filters?.date?.from, "dd/MM/yy")
                                      : ""
                                  }${
                                    filters?.date?.to
                                      ? ` to ${format(filters?.date?.to, "dd/MM/yy")}`
                                      : ""
                                  }`
                                : null
                            }
                          >
                            {cloneElement(child, {
                              filter: filters?.date,
                              setFilter: handleChange,
                            })}
                          </FilterBase>
                        );
                      case AmountFilter:
                        return (
                          <FilterBase
                            label="Amount"
                            index={index}
                            defaultExpanded={index === 0}
                            subHeading={
                              filters?.amount?.from || filters?.amount?.to
                                ? `${
                                    filters?.amount?.from && !isNaN(filters?.amount?.from)
                                      ? filters?.amount?.from
                                      : 0
                                  }${
                                    filters?.amount?.to && !isNaN(filters?.amount?.to)
                                      ? ` to ${filters?.amount?.to}`
                                      : ""
                                  }`
                                : null
                            }
                          >
                            {cloneElement(child, {
                              filter: filters.amount,
                              setFilter: handleChange,
                            })}
                          </FilterBase>
                        );
                      default:
                        return console.warn(
                          "Only the following components are allowed as children: `Filters.Status`, `Filters.Date`, `Filters.Amount`",
                        );
                    }
                  })}
                </div>
              </Paper>
            </Fade>
          )}
        </Popper>
        <Button
          onClick={handleClick}
          classes={{ root: classes.button }}
          style={{ position: "relative" }}
          startIcon={
            <SvgIcon
              fontSize="small"
              component={FiltersIcon}
              style={{ fontSize: 16 }}
              color="primary"
            />
          }
        >
          <span className={classes.buttonLabel}>Filters</span>
          {Boolean(filtersCount) && <Box className={classes.badge}>{filtersCount}</Box>}
        </Button>
        {Boolean(filtersCount) && (
          <ButtonBase className={classes.clear} onClick={handleClear}>
            <ClearIcon />
          </ButtonBase>
        )}
      </div>
    </ClickAwayListener>
  );
};

/** more filters can be added here */
Filters.Status = StatusFilter;
Filters.Date = DateFilter;
Filters.Amount = AmountFilter;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "inline-flex",
      alignItems: "center",
    },
    contentRoot: {
      width: 350,
      maxHeight: "75vh",
      overflowY: "auto",
    },
    buttonLabel: {
      display: "block",
      alignSelf: "flex-end",
      lineHeight: 1,
      marginLeft: 4,
    },
    clear: {
      marginLeft: 4,
    },
    badge: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginLeft: 4,
      minWidth: 16,
      paddingRight: 0,
      paddingLeft: 2,
      textAlign: "center",
      height: 16,
      borderRadius: 999,

      fontSize: 10,
      lineHeight: 1,
      fontWeight: 600,
      color: "white",

      backgroundColor: theme.palette.primary.main,
      boxShadow: "0 4px 6px 0 rgba(3, 100, 255, 0.3)",
    },
    button: {
      padding: `${theme.spacing(1)}px 0`,
      fontSize: 12,
      lineHeight: "16px",
      color: theme.palette.dark.main,
      textTransform: "uppercase",
      fontWeight: "bold",

      transition: "color ease-in .18s",

      "&:hover, &:active": {
        background: "transparent",
        color: theme.palette.battleshipGrey.main,
      },
    },
    popperRow: {
      margin: "0 1px 0 0",
      padding: 13,
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(3),
      borderRadius: "4px",
      boxShadow: "0 10px 12px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(10, 31, 68, 0.1)",
      backgroundColor: "white",
    },
  }),
);
